// MACHINERY V2 (Stroje) — the detail page's image + thumbnail strip (M2.8).
// Split out of CatalogDetailAside.tsx for the 300-line cap.
//
// Spec: data package §11 `images` row. Artboard 0902 draws the single large
// image; the strip appears only when the model has more than one photo.
//
// >>> THE HERO COMES FROM THE MODEL ROW, EXTRAS FROM `images`. <<<
// §11 says "detail gallery (sort displayOrder, src imageDetailUrl||
// imageStandardUrl)", but the live page carries a LATER fix with a measured
// blast radius, and it is the correct one: `model.images` holds a DENORMALISED
// COPY of the hero (`imageType === "hero"`) alongside the real extra angle
// shots, and when the model row is repointed without rewriting that copy the
// two disagree — preferring the copy made the DETAIL page show a stale photo
// while the browse CARD showed the right one. So the hero is taken from the
// model row and only NON-hero rows contribute extras. Reproduced behaviour for
// behaviour from CatalogModelDetailPage.tsx:150-173 (`catalogGalleryUrls` and
// the comment above it), including the two easy-to-miss halves: the hero row is
// skipped ONLY when the model row actually produced a hero (otherwise a
// hero-typed image is the only photo there is), and identical URLs are
// de-duplicated.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo, useState } from "react";

import { CatalogModelDetail } from "../../models/models";
import { CatalogThumb } from "./catalogAtoms";

export const buildCatalogGallery = (model: CatalogModelDetail): string[] => {
  const heroUrl = model.imageDetailUrl || model.imageStandardUrl || model.imageUrl || null;
  const urls: string[] = heroUrl ? [heroUrl] : [];

  [...(model.images || [])]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .forEach((img) => {
      if (heroUrl && img.imageType === "hero") return;
      const url = img.imageDetailUrl || img.imageStandardUrl;
      if (!url || urls.indexOf(url) >= 0) return;
      urls.push(url);
    });

  return urls;
};

export interface CatalogDetailGalleryProps {
  model: CatalogModelDetail;
}

const CatalogDetailGallery: React.FC<CatalogDetailGalleryProps> = ({ model }) => {
  const [active, setActive] = useState(0);
  const gallery = useMemo(() => buildCatalogGallery(model), [model]);

  return (
    <React.Fragment>
      <CatalogThumb
        url={gallery[active] || null}
        alt={model.modelName}
        width="100%"
        height={210}
        radius={0}
      />

      {gallery.length > 1 && (
        <div
          style={{ display: "flex", gap: 6, padding: "8px 10px 0", flexWrap: "wrap", minWidth: 0 }}
        >
          {gallery.map((url, index) => (
            <button
              key={url}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`${model.modelName} ${index + 1}`}
              aria-current={index === active ? "true" : undefined}
              style={{
                width: 44,
                height: 32,
                padding: 0,
                borderRadius: 6,
                overflow: "hidden",
                cursor: "pointer",
                background: "var(--card-2)",
                border: "1px solid " + (index === active ? "var(--mod-accent)" : "var(--border)"),
              }}
            >
              <img
                src={url}
                alt=""
                loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
              />
            </button>
          ))}
        </div>
      )}
    </React.Fragment>
  );
};

export default CatalogDetailGallery;
